import React from "react";
import { Alert, Button, Col, Input, InputNumber, Row, Segmented, Select, Typography } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";

import { InvoiceLineSourceType } from "../../../types/finance/FinanceTypes";
import { PriceEntryMode, TAX_CODE_LABELS, TaxCode } from "../../../utils/finance/gst";
import { formatCents } from "../../../utils/finance/money";
import CatalogueLineSelect from "./CatalogueLineSelect";
import type { CataloguePick, InvoiceLineDraft } from "./invoiceLineDraft";
import {
	applyCataloguePick,
	lineSourceType,
	newLineDraft,
	previewDraftLine,
	previewDraftTotals,
	setLineSource,
} from "./invoiceLineDraft";
import type { UserUuid } from "../../../types/identity";

const { Text } = Typography;

/**
 * The line-item editor for an invoice draft.
 *
 * ── Controlled, so it drops straight into a `Form.Item` ──────────────────────────────────────
 *
 * `value` / `onChange` are antd's form contract, so this renders as
 * `<Form.Item name="lines"><InvoiceLineEditor mode={…} /></Form.Item>` and the parent form owns
 * the state. It is deliberately NOT a `Form.List`: a `Form.List` keeps the rows inside antd's
 * store, and reading them back on every keystroke to recompute totals means either a
 * `Form.useWatch` on a nested path or a `shouldUpdate` re-render of the whole form. A plain
 * controlled array is simpler and makes the live totals a pure function of the value.
 *
 * ── The totals under the table are a PREVIEW ─────────────────────────────────────────────────
 *
 * They come from `utils/finance/gst.ts`, the port of the backend engine, driven by the same
 * fixture table — so they agree to the cent. They are still only a preview: the request carries
 * quantity/unit price/tax code and the server computes and returns the authoritative figures,
 * which replace these the moment the mutation resolves.
 *
 * ── The per-line source selector (S12) ───────────────────────────────────────────────────────
 *
 * Manual · Product · Appointment type. The two catalogue arms render `CatalogueLineSelect`; every
 * decision either of them makes about the draft is a pure function in `invoiceLineDraft.ts`
 * (`setLineSource`, `applyCataloguePick`) — nothing about the prefill or the provenance is decided
 * in this file, because antd v5 dropdowns cannot be opened under jsdom and logic living here could
 * not be tested at all.
 *
 * A pick fills the description, the price and the tax code and then gets out of the way: all three
 * stay editable (parent decision D4), and the catalogue's own price is snapshotted server-side so
 * a discount reads as a variance rather than erasing it.
 */

export interface InvoiceLineEditorProps {
	value?: InvoiceLineDraft[];
	onChange?: (lines: InvoiceLineDraft[]) => void;
	/** Decides the price-input label and the arithmetic. Changing it re-previews every line. */
	mode: PriceEntryMode;
	/** The clinic's default tax code, applied to a newly added line. */
	defaultTaxCode?: TaxCode;
	/**
	 * Whose rates price the appointment-type lines (decision D7). It MUST be the same value the
	 * create/update request carries, or the price quoted here and the price the server snapshots
	 * are two different numbers — `CreateInvoiceModal` resolves it once and hands it to both.
	 */
	practitionerId?: UserUuid;
	disabled?: boolean;
}

const TAX_CODE_OPTIONS = (Object.keys(TAX_CODE_LABELS) as TaxCode[]).map((code) => ({
	value: code,
	label: TAX_CODE_LABELS[code],
}));

const SOURCE_OPTIONS = [
	{ value: InvoiceLineSourceType.MANUAL, label: "Manual" },
	{ value: InvoiceLineSourceType.COMMERCE_PRODUCT, label: "Product" },
	{ value: InvoiceLineSourceType.APPOINTMENT_TYPE, label: "Appointment type" },
];

const InvoiceLineEditor: React.FC<InvoiceLineEditorProps> = ({
	value,
	onChange,
	mode,
	defaultTaxCode = TaxCode.GST_FREE_HEALTH,
	practitionerId,
	disabled,
}) => {
	// ⚠️ The fallback row is built ONCE, in a ref, not inline in the render expression.
	// `newLineDraft()` mints a fresh `key` on every call, so an inline fallback would hand every
	// render a different key, React would unmount and remount the row, and the operator would lose
	// focus (and the character they were typing) on every keystroke before the first `onChange`.
	const fallbackRef = React.useRef<InvoiceLineDraft[]>([newLineDraft(defaultTaxCode)]);
	const lines = value && value.length > 0 ? value : fallbackRef.current;

	const emit = (next: InvoiceLineDraft[]) => onChange?.(next);

	const patch = (index: number, changes: Partial<InvoiceLineDraft>) => {
		emit(lines.map((line, i) => (i === index ? { ...line, ...changes } : line)));
	};

	/** Replace one line WHOLESALE — the pure helpers return a new draft, not a patch. */
	const replace = (index: number, next: InvoiceLineDraft) => {
		emit(lines.map((line, i) => (i === index ? next : line)));
	};

	const priceLabel =
		mode === PriceEntryMode.EXCLUSIVE ? "Unit price (ex. GST)" : "Unit price (inc. GST)";

	const totals = previewDraftTotals(lines, mode);

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
			{/*
			  The description is printed verbatim on the document the patient receives, and on the
			  Stripe hosted page for a Stripe-collected invoice. Operators habitually type
			  shorthand clinical notes into free-text fields; this is the one place to say plainly
			  that this particular field leaves the clinic.
			*/}
			<Alert
				type="info"
				showIcon
				message="The patient sees each description exactly as typed — keep it to the service billed."
				style={{ fontSize: 12 }}
			/>

			{lines.map((line, index) => {
				const amounts = previewDraftLine(line, mode);

				return (
					<div
						key={line.key}
						style={{
							border: "1px solid var(--mg-border)",
							borderRadius: 8,
							padding: 12,
						}}
					>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								marginBottom: 8,
							}}
						>
							<Text strong style={{ fontSize: 13 }}>
								Line {index + 1}
							</Text>
							{lines.length > 1 && (
								<Button
									type="text"
									danger
									size="small"
									icon={<DeleteOutlined />}
									disabled={disabled}
									onClick={() => emit(lines.filter((_, i) => i !== index))}
									data-testid={`invoice-line-remove-${index}`}
								>
									Remove
								</Button>
							)}
						</div>

						<Row gutter={[12, 8]}>
							<Col xs={24}>
								<Text type="secondary" style={{ fontSize: 12 }}>
									Source
								</Text>
								<Segmented
									block
									value={lineSourceType(line)}
									options={SOURCE_OPTIONS}
									disabled={disabled}
									onChange={(next) =>
										replace(index, setLineSource(line, next as InvoiceLineSourceType))
									}
									data-testid={`invoice-line-source-${index}`}
								/>
							</Col>
							{lineSourceType(line) !== InvoiceLineSourceType.MANUAL && (
								<Col xs={24}>
									<CatalogueLineSelect
										line={line}
										sourceType={lineSourceType(line)}
										practitionerId={practitionerId}
										index={index}
										disabled={disabled}
										onPick={(pick: CataloguePick) =>
											replace(index, applyCataloguePick(line, pick, defaultTaxCode))
										}
									/>
								</Col>
							)}
							<Col xs={24}>
								<Text type="secondary" style={{ fontSize: 12 }}>
									Description
								</Text>
								<Input
									value={line.description}
									disabled={disabled}
									placeholder="e.g. Initial consultation"
									onChange={(e) => patch(index, { description: e.target.value })}
									data-testid={`invoice-line-description-${index}`}
								/>
							</Col>
							<Col xs={12} md={5}>
								<Text type="secondary" style={{ fontSize: 12 }}>
									Quantity
								</Text>
								<InputNumber
									value={line.quantity}
									min={1}
									precision={0}
									disabled={disabled}
									style={{ width: "100%" }}
									onChange={(qty) => patch(index, { quantity: Number(qty ?? 1) })}
									data-testid={`invoice-line-quantity-${index}`}
								/>
							</Col>
							<Col xs={12} md={7}>
								<Text type="secondary" style={{ fontSize: 12 }}>
									{priceLabel}
								</Text>
								<InputNumber
									value={line.unitPriceDollars}
									step={0.01}
									prefix="$"
									disabled={disabled}
									style={{ width: "100%" }}
									placeholder="0.00"
									onChange={(price) =>
										patch(index, {
											unitPriceDollars:
												price === null || price === undefined ? null : Number(price),
										})
									}
									data-testid={`invoice-line-price-${index}`}
								/>
							</Col>
							<Col xs={24} md={12}>
								<Text type="secondary" style={{ fontSize: 12 }}>
									Tax treatment
								</Text>
								<Select
									value={line.taxCode}
									options={TAX_CODE_OPTIONS}
									disabled={disabled}
									style={{ width: "100%" }}
									onChange={(taxCode) => patch(index, { taxCode: taxCode as TaxCode })}
									data-testid={`invoice-line-taxcode-${index}`}
								/>
							</Col>
						</Row>

						<div
							style={{
								display: "flex",
								justifyContent: "flex-end",
								gap: 16,
								marginTop: 8,
								fontSize: 12,
							}}
							data-testid={`invoice-line-total-${index}`}
						>
							<Text type="secondary">Ex. GST {formatCents(amounts.lineExCents)}</Text>
							<Text type="secondary">GST {formatCents(amounts.lineTaxCents)}</Text>
							<Text strong>Total {formatCents(amounts.lineIncCents)}</Text>
						</div>
					</div>
				);
			})}

			<Button
				type="dashed"
				block
				icon={<PlusOutlined />}
				disabled={disabled}
				onClick={() => emit([...lines, newLineDraft(defaultTaxCode)])}
				data-testid="invoice-line-add"
			>
				Add line
			</Button>

			<div
				style={{
					borderTop: "1px solid var(--mg-border)",
					paddingTop: 8,
					display: "flex",
					flexDirection: "column",
					alignItems: "flex-end",
					gap: 2,
				}}
				data-testid="invoice-line-totals"
			>
				<Text type="secondary" style={{ fontSize: 12 }}>
					Subtotal (ex. GST) {formatCents(totals.subtotalExCents)}
				</Text>
				<Text type="secondary" style={{ fontSize: 12 }}>
					GST {formatCents(totals.taxCents)}
				</Text>
				<Text strong style={{ fontSize: 14 }}>
					Total {formatCents(totals.totalIncCents)}
				</Text>
				<Text type="secondary" style={{ fontSize: 11 }}>
					Preview only — the saved invoice carries the server's figures.
				</Text>
			</div>
		</div>
	);
};

export default InvoiceLineEditor;
